import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Boxes,
  CalendarClock,
  CheckCircle2,
  CircleDollarSign,
  FileText,
  History,
  LayoutList,
  Pencil,
  Plus,
  Save,
  Search,
  Truck,
  X,
} from 'lucide-react';
import {
  FINANCIAL_ENTRY_STATUS_LABELS,
  FISCAL_SITUATION_LABELS,
  formatCents,
  formatDocument,
  formatQty,
  parseQty,
  type FinancialEntry,
  type FiscalDocument,
  type FiscalDocumentItem,
  type Item,
  type ReconcileCandidate,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { blank, formatDate, formatDateTime } from '@/lib/format';
import { Button } from '@/components/Button';
import { StatusBadge } from '@/components/StatusBadge';
import { ErrorState, LoadingState } from '@/components/states';
import { useItems, useStockLocations } from '@/lib/queries';
import { LancarDocumentoDrawer, NovoItemModal } from './DocumentoLancamento';
import { SITUATION_TONE, documentoPath } from './documentos-shared';

/**
 * Conciliação da nota de entrada, pelos dois lados:
 *
 * - FINANCEIRO: a nota casa com a conta a pagar que já existe (o boleto foi
 *   digitado antes do XML chegar) ou gera a conta nova.
 * - ITENS: cada linha da nota aponta para o item do estoque, e a nota dá
 *   entrada no almoxarifado.
 *
 * As regras são as mesmas do botão "Lançar" da nota — esta tela só é outro
 * caminho até elas. Nada aqui calcula dinheiro por conta própria.
 */

/* ------------------------------- casca comum ------------------------------ */

function Janela({
  title,
  description,
  wide,
  onClose,
  children,
  footer,
}: {
  title: string;
  description: string;
  wide?: boolean;
  onClose: () => void;
  children: ReactNode;
  footer: ReactNode;
}) {
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <>
      <div className="overlay overlay--modal" onClick={onClose} aria-hidden />
      <div
        className={`modal nf-janela${wide ? ' nf-janela--larga' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="nf-janela__cab">
          <div>
            <h2 className="nf-janela__titulo">{title}</h2>
            <p className="nf-janela__desc">{description}</p>
          </div>
          <button type="button" className="ctxpanel__close" onClick={onClose} aria-label="Fechar janela">
            <X size={18} aria-hidden />
          </button>
        </header>
        {children}
        <footer className="nf-janela__rodape">{footer}</footer>
      </div>
    </>
  );
}

function useDocumento(documentId: string) {
  return useQuery({
    queryKey: ['fiscal-document', documentId],
    queryFn: () => api.get<FiscalDocument>(`/fiscal-documents/${documentId}`),
  });
}

function Dado({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="nf-dado">
      <span className="nf-dado__rotulo">{label}</span>
      <span className="nf-dado__valor">{children}</span>
    </div>
  );
}

/* ========================= conciliação financeira ========================= */

type Aba = 'resumo' | 'pagamentos' | 'historico';

export function ConciliacaoFinanceiraModal({
  documentId,
  onClose,
  onChanged,
}: {
  documentId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const navigate = useNavigate();
  const documento = useDocumento(documentId);
  const doc = documento.data;

  const [aba, setAba] = useState<Aba>('resumo');
  const [escolhido, setEscolhido] = useState('');
  const [aceitaDiferenca, setAceitaDiferenca] = useState(false);
  const [criando, setCriando] = useState(false);

  const pendente = doc?.financialStatus === 'PENDENTE';

  const candidatos = useQuery({
    queryKey: ['fiscal-document', documentId, 'reconcile-candidates'],
    queryFn: () =>
      api.get<ReconcileCandidate[]>(`/fiscal-documents/${documentId}/reconcile-candidates`),
    enabled: pendente,
  });

  const lancamento = useQuery({
    queryKey: ['financial-entries', doc?.financialEntryId],
    queryFn: () => api.get<FinancialEntry>(`/financial-entries/${doc!.financialEntryId}`),
    enabled: Boolean(doc?.financialEntryId),
  });

  const conciliar = useMutation({
    mutationFn: (body: unknown) =>
      api.post(`/fiscal-documents/${documentId}/reconcile-financial`, body),
    onSuccess: () => {
      onChanged();
      void documento.refetch();
      setEscolhido('');
    },
  });

  const lista = candidatos.data ?? [];
  const candidato = lista.find((c) => c.id === escolhido) ?? null;
  const conciliadoCents = lancamento.data?.totalCents ?? candidato?.totalCents ?? 0;
  const diferenca = (doc?.totalCents ?? 0) - conciliadoCents;
  const podeSalvar =
    pendente && Boolean(candidato) && (candidato!.differenceCents === 0 || aceitaDiferenca);

  return (
    <>
      <Janela
        title="Conciliação financeira da nota"
        description="Veja a nota e amarre-a à conta a pagar — a que já existe ou uma nova."
        onClose={onClose}
        footer={
          <>
            <Button
              variant="outline"
              icon={Pencil}
              onClick={() => void navigate({ to: documentoPath(documentId) })}
            >
              Abrir a nota
            </Button>
            <span style={{ flex: 1 }} />
            <Button variant="outline" onClick={onClose}>
              Fechar
            </Button>
            {pendente && (
              <Button
                variant="primary"
                icon={CheckCircle2}
                disabled={!podeSalvar}
                loading={conciliar.isPending}
                onClick={() =>
                  conciliar.mutate({
                    entryId: candidato!.id,
                    ...(candidato!.differenceCents === 0
                      ? {}
                      : { acceptDifferenceCents: candidato!.differenceCents }),
                  })
                }
              >
                Salvar conciliação
              </Button>
            )}
          </>
        }
      >
        {documento.isPending ? (
          <LoadingState label="Carregando a nota…" />
        ) : documento.isError ? (
          <ErrorState message={errorMessage(documento.error)} />
        ) : (
          <>
            <div className="nf-cabecalho">
              <Dado label="Número da nota">
                <strong className="mono">
                  {doc!.number}
                  {doc!.series ? `/${doc!.series}` : ''}
                </strong>
              </Dado>
              <Dado label="Fornecedor">{doc!.supplierTradeName ?? doc!.supplierName}</Dado>
              <Dado label="CNPJ/CPF">
                <span className="mono">
                  {doc!.supplierDocument ? formatDocument(doc!.supplierDocument) : '—'}
                </span>
              </Dado>
              <Dado label="Valor total">
                <strong className="mono">{formatCents(doc!.totalCents)}</strong>
              </Dado>
              <Dado label="Conciliação financeira">
                {doc!.financialStatus === 'LANCADO' ? (
                  <StatusBadge kind="ok">Conciliada</StatusBadge>
                ) : doc!.financialStatus === 'PENDENTE' ? (
                  <StatusBadge kind="warn">Pendente</StatusBadge>
                ) : (
                  <StatusBadge kind="mute">Não se aplica</StatusBadge>
                )}
              </Dado>
              <Dado label="Data de emissão">
                <span className="mono">{formatDate(doc!.issueDate)}</span>
              </Dado>
              <Dado label="Data de entrada">
                <span className="mono">{doc!.entryDate ? formatDate(doc!.entryDate) : '—'}</span>
              </Dado>
            </div>

            <div className="abas nf-abas" role="tablist" aria-label="Conciliação financeira">
              {(
                [
                  ['resumo', 'Resumo', LayoutList],
                  ['pagamentos', 'Pagamentos', CalendarClock],
                  ['historico', 'Histórico', History],
                ] as const
              ).map(([valor, rotulo, Icon]) => (
                <button
                  key={valor}
                  type="button"
                  role="tab"
                  aria-selected={aba === valor}
                  className={`aba${aba === valor ? ' is-active' : ''}`}
                  onClick={() => setAba(valor)}
                >
                  <Icon size={14} aria-hidden /> {rotulo}
                </button>
              ))}
            </div>

            {conciliar.isError && (
              <div className="login__alert" role="alert" style={{ margin: '12px 0' }}>
                {errorMessage(conciliar.error)}
              </div>
            )}

            {aba === 'resumo' && (
              <>
                <div className="nf-secao-cab">
                  <div>
                    <h3 className="nf-secao-titulo">Lançamentos financeiros (contas a pagar)</h3>
                    <p className="nf-secao-desc">
                      {pendente
                        ? 'Vincule a conta a pagar desta nota. O valor conciliado deve ser igual ao total da nota.'
                        : 'A conta a pagar que esta nota originou ou conciliou.'}
                    </p>
                  </div>
                  {pendente && (
                    <Button icon={Plus} onClick={() => setCriando(true)}>
                      Criar novo lançamento
                    </Button>
                  )}
                </div>

                {doc!.financialEntryId ? (
                  <table className="nf-tabela">
                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Descrição</th>
                        <th>Vencimento</th>
                        <th className="is-right">Valor</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lancamento.data ? (
                        <tr>
                          <td className="mono">{lancamento.data.code}</td>
                          <td>{lancamento.data.description}</td>
                          <td className="mono">
                            {lancamento.data.nextDueDate
                              ? formatDate(lancamento.data.nextDueDate)
                              : '—'}
                          </td>
                          <td className="is-right mono">
                            {formatCents(lancamento.data.totalCents)}
                          </td>
                          <td>
                            <StatusBadge kind={lancamento.data.status === 'PAID' ? 'ok' : 'warn'}>
                              {FINANCIAL_ENTRY_STATUS_LABELS[lancamento.data.status]}
                            </StatusBadge>
                          </td>
                        </tr>
                      ) : (
                        <tr>
                          <td colSpan={5} className="muted">
                            {doc!.financialEntryCode ?? 'Carregando…'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                ) : !pendente ? (
                  <p className="nf-vazio">
                    Este documento não gera conta a pagar (
                    {doc!.financialStatus === 'BLOQUEADO' ? 'bloqueado' : 'não se aplica'}).
                  </p>
                ) : candidatos.isPending ? (
                  <LoadingState label="Procurando contas a pagar parecidas…" />
                ) : candidatos.isError ? (
                  // Falha na busca não é "não há parecida": dizer isso levaria a
                  // criar uma conta em duplicidade da que já existe.
                  <ErrorState
                    title="Não foi possível procurar as contas a pagar parecidas"
                    message={errorMessage(candidatos.error)}
                    onRetry={() => void candidatos.refetch()}
                  />
                ) : lista.length === 0 ? (
                  <p className="nf-vazio">
                    Nenhuma conta a pagar parecida com esta nota (mesmo fornecedor, valor ou data
                    próxima). Use <strong>Criar novo lançamento</strong>.
                  </p>
                ) : (
                  <table className="nf-tabela">
                    <thead>
                      <tr>
                        <th>Fornecedor</th>
                        <th>Descrição</th>
                        <th>Emissão</th>
                        <th className="is-right">Valor</th>
                        <th>Status</th>
                        <th className="is-right">Ação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lista.map((linha) => (
                        <tr key={linha.id} className={escolhido === linha.id ? 'is-escolhida' : ''}>
                          <td>{blank(linha.personName)}</td>
                          <td>
                            <span className="mono">{linha.code}</span> · {linha.description}
                            {/* O motivo é o que deixa DISCORDAR da sugestão. */}
                            <div className="muted" style={{ fontSize: 11.5 }}>
                              {linha.reasons.join(' · ')}
                            </div>
                          </td>
                          <td className="mono">{formatDate(linha.issueDate)}</td>
                          <td className="is-right mono">{formatCents(linha.totalCents)}</td>
                          <td>
                            <StatusBadge kind="warn">Pendente</StatusBadge>
                          </td>
                          <td className="is-right">
                            <Button
                              small
                              variant={escolhido === linha.id ? 'primary' : 'outline'}
                              onClick={() => {
                                setEscolhido(escolhido === linha.id ? '' : linha.id);
                                setAceitaDiferenca(false);
                              }}
                            >
                              {escolhido === linha.id ? 'Vinculado' : 'Vincular'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                <div className="nf-saldo">
                  <span className="nf-saldo__icone" aria-hidden>
                    <CircleDollarSign size={22} />
                  </span>
                  <Dado label="Valor total da nota">
                    <strong className="mono">{formatCents(doc!.totalCents)}</strong>
                  </Dado>
                  <Dado label="Valor conciliado">
                    <strong
                      className="mono"
                      style={{ color: diferenca === 0 && conciliadoCents > 0 ? 'var(--success-text)' : undefined }}
                    >
                      {formatCents(conciliadoCents)}
                    </strong>
                  </Dado>
                  <div className="nf-saldo__status">
                    {conciliadoCents === 0 ? (
                      <span className="muted">Nenhuma conta vinculada ainda.</span>
                    ) : diferenca === 0 ? (
                      <span className="nf-ok">
                        <CheckCircle2 size={18} aria-hidden /> Conciliação completa
                      </span>
                    ) : (
                      <span style={{ color: 'var(--danger-text)' }}>
                        Diferença de {formatCents(Math.abs(diferenca))} — a nota é{' '}
                        {diferenca > 0 ? 'maior' : 'menor'} que a conta.
                      </span>
                    )}
                  </div>
                </div>

                {candidato && candidato.differenceCents !== 0 && (
                  <label className="nf-aceite">
                    <input
                      type="checkbox"
                      checked={aceitaDiferenca}
                      onChange={(event) => setAceitaDiferenca(event.target.checked)}
                    />
                    <span>
                      Conciliar mesmo com a diferença de{' '}
                      <strong>{formatCents(Math.abs(candidato.differenceCents))}</strong>. O valor da
                      conta a pagar <strong>não muda</strong> — se a nota estiver certa, corrija-a
                      depois no Financeiro.
                    </span>
                  </label>
                )}
              </>
            )}

            {aba === 'pagamentos' && (
              <Pagamentos lancamento={lancamento.data ?? null} temConta={Boolean(doc!.financialEntryId)} />
            )}
            {aba === 'historico' && <Historico id={documentId} />}
          </>
        )}
      </Janela>

      {criando && doc && (
        <LancarDocumentoDrawer
          document={doc}
          somente="financeiro"
          onClose={() => setCriando(false)}
          onPosted={() => {
            setCriando(false);
            onChanged();
            void documento.refetch();
          }}
        />
      )}
    </>
  );
}

function Pagamentos({ lancamento, temConta }: { lancamento: FinancialEntry | null; temConta: boolean }) {
  if (!temConta) {
    return (
      <p className="nf-vazio">Ainda não há conta a pagar desta nota — concilie ou crie uma no Resumo.</p>
    );
  }
  if (!lancamento) return <LoadingState label="Carregando as parcelas…" />;
  return (
    <table className="nf-tabela">
      <thead>
        <tr>
          <th>Parcela</th>
          <th>Vencimento</th>
          <th className="is-right">Valor</th>
          <th className="is-right">Pago</th>
          <th>Baixas</th>
        </tr>
      </thead>
      <tbody>
        {lancamento.installments.map((parcela) => (
          <tr key={parcela.id}>
            <td className="mono">
              {parcela.number}/{lancamento.installments.length}
            </td>
            <td className="mono">{formatDate(parcela.dueDate)}</td>
            <td className="is-right mono">{formatCents(parcela.amountCents)}</td>
            <td className="is-right mono">{formatCents(parcela.paidCents)}</td>
            <td>
              {parcela.payments.length === 0 ? (
                <span className="muted">—</span>
              ) : (
                parcela.payments.map((pagamento) => (
                  <div key={pagamento.id} style={{ fontSize: 12 }}>
                    {pagamento.isReversal ? 'Estorno' : 'Baixa'} {formatDate(pagamento.paidAt)} ·{' '}
                    {formatCents(pagamento.amountCents)} · {blank(pagamento.bankAccountName)}
                  </div>
                ))
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Historico({ id }: { id: string }) {
  const query = useQuery({
    queryKey: ['fiscal-document', id, 'timeline'],
    queryFn: () =>
      api.get<
        Array<{ id: string; action: string; userName: string | null; at: string; reason: string | null }>
      >(`/fiscal-documents/${id}/timeline`),
  });
  const rotulo: Record<string, string> = {
    CREATE: 'Nota registrada',
    UPDATE: 'Nota alterada',
    APPROVE: 'Conferência / lançamento',
    REJECT: 'Cancelamento / ignorada',
    DELETE: 'Nota excluída',
  };
  if (query.isPending) return <LoadingState label="Carregando o histórico…" />;
  if (query.isError) return <ErrorState message={errorMessage(query.error)} />;
  if (query.data.length === 0) return <p className="nf-vazio">Nada registrado ainda.</p>;
  return (
    <ol className="nf-historico">
      {query.data.map((linha) => (
        <li key={linha.id}>
          <strong>{rotulo[linha.action] ?? linha.action}</strong>
          <span>
            {blank(linha.userName)} · {formatDateTime(linha.at)}
          </span>
          {linha.reason && <span>{linha.reason}</span>}
        </li>
      ))}
    </ol>
  );
}

/* =========================== conciliação de itens ========================= */

export function ConciliacaoItensModal({
  documentId,
  onClose,
  onChanged,
}: {
  documentId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const documento = useDocumento(documentId);
  const doc = documento.data;
  if (documento.isPending) {
    return (
      <Janela title="Conciliação de itens da nota" description="" wide onClose={onClose} footer={null}>
        <LoadingState label="Carregando a nota…" />
      </Janela>
    );
  }
  if (documento.isError || !doc) {
    return (
      <Janela title="Conciliação de itens da nota" description="" wide onClose={onClose} footer={null}>
        <ErrorState message={errorMessage(documento.error)} />
      </Janela>
    );
  }
  // Montado só com a nota carregada: o estado dos vínculos nasce dela.
  return <ItensDaNota doc={doc} onClose={onClose} onChanged={onChanged} />;
}

function ItensDaNota({
  doc,
  onClose,
  onChanged,
}: {
  doc: FiscalDocument;
  onClose: () => void;
  onChanged: () => void;
}) {
  const navigate = useNavigate();
  const [busca, setBusca] = useState('');
  const catalogo = useItems(busca);
  const locais = useStockLocations();

  /**
   * Por que a nota pode NÃO dar entrada aqui. Com pedido ou recebimento, a
   * quantidade verdadeira é a ACEITA na portaria — entrar de novo dobraria o
   * saldo. Estas regras são as mesmas do "Lançar" da nota.
   */
  const coberto = doc.orders.length > 0 || doc.receivings.length > 0;
  const bloqueio =
    doc.items.length === 0
      ? 'Este documento não tem itens — não há o que vincular ao estoque.'
      : doc.stockStatus === 'LANCADO'
        ? 'Os itens desta nota já deram entrada no estoque.'
        : doc.stockStatus === 'COBERTO' || coberto
          ? 'Esta nota está amarrada a pedido ou recebimento: o estoque entra pelo recebimento, com a quantidade aceita na portaria.'
          : doc.stockStatus !== 'PENDENTE'
            ? 'Esta nota não dá entrada de estoque.'
            : doc.hasDivergence
              ? 'A nota tem divergência aberta. Trate a divergência na página da nota antes de dar entrada.'
              : null;
  const somenteLeitura = Boolean(bloqueio);

  const [links, setLinks] = useState<Record<string, string>>(() =>
    Object.fromEntries(doc.items.map((l) => [l.id, l.itemId ?? l.suggestedItemId ?? ''])),
  );
  const [rotulos, setRotulos] = useState<Record<string, Pick<Item, 'id' | 'sku' | 'name' | 'unit' | 'tracksBatch'>>>(
    () =>
      Object.fromEntries(
        doc.items
          .filter((l) => !l.itemId && l.suggestedItemId)
          .map((l) => {
            const [sku = '', ...resto] = (l.suggestedItemLabel ?? '').split(' · ');
            return [
              l.suggestedItemId!,
              {
                id: l.suggestedItemId!,
                sku,
                name: resto.join(' · '),
                unit: l.suggestedItemUnit ?? l.unit,
                tracksBatch: l.suggestedItemTracksBatch ?? false,
              },
            ];
          }),
      ),
  );
  const [fatores, setFatores] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      doc.items
        .filter((l) => !l.itemId && l.suggestedFactorMilli)
        .map((l) => [l.id, formatQty(l.suggestedFactorMilli!)]),
    ),
  );
  const [lotes, setLotes] = useState<Record<string, { code: string; expiry: string }>>({});
  const [locationId, setLocationId] = useState('');
  const [linhaAtiva, setLinhaAtiva] = useState<string>(
    () => doc.items.find((l) => !(l.itemId ?? l.suggestedItemId))?.id ?? doc.items[0]?.id ?? '',
  );
  const [cadastrando, setCadastrando] = useState<FiscalDocumentItem | null>(null);

  const conhecido = (id: string) =>
    rotulos[id] ?? (catalogo.data ?? []).find((item) => item.id === id) ?? null;

  const vincular = (lineId: string, item: Pick<Item, 'id' | 'sku' | 'name' | 'unit' | 'tracksBatch'>) => {
    setLinks((atual) => ({ ...atual, [lineId]: item.id }));
    setRotulos((atual) => ({ ...atual, [item.id]: item }));
    // Fator de um item não vale para outro: trocar mantendo o fator aplicaria
    // a embalagem de um no outro — saldo e custo errados, em silêncio.
    setFatores((atual) => {
      const { [lineId]: _antigo, ...resto } = atual;
      return resto;
    });
    // Vai para a próxima linha sem vínculo: é o ritmo de quem confere nota.
    const proxima = doc.items.find((l) => l.id !== lineId && !links[l.id]);
    if (proxima) setLinhaAtiva(proxima.id);
  };

  const desvincular = (lineId: string) => setLinks((atual) => ({ ...atual, [lineId]: '' }));

  const unidadeDiferente = (line: FiscalDocumentItem) => {
    const item = links[line.id] ? conhecido(links[line.id]!) : null;
    return item ? item.unit !== line.unit : false;
  };
  const exigeLote = (line: FiscalDocumentItem) => {
    const item = links[line.id] ? conhecido(links[line.id]!) : null;
    return item?.tracksBatch ?? false;
  };

  const vinculadas = doc.items.filter((l) => links[l.id]);
  const progresso = doc.items.length === 0 ? 0 : Math.round((vinculadas.length / doc.items.length) * 100);
  const semVinculo = doc.items.length - vinculadas.length;

  const prontas =
    Boolean(locationId) &&
    vinculadas.length > 0 &&
    vinculadas.every((l) => !unidadeDiferente(l) || (parseQty(fatores[l.id] ?? '') ?? 0) > 0) &&
    vinculadas.every((l) => !exigeLote(l) || (lotes[l.id]?.code ?? '').trim());

  const salvar = useMutation({
    mutationFn: () =>
      api.post(`/fiscal-documents/${doc.id}/post`, {
        toFinancial: false,
        toStock: true,
        stock: {
          locationId,
          links: vinculadas.map((l) => ({
            documentItemId: l.id,
            itemId: links[l.id],
            ...(unidadeDiferente(l)
              ? { conversionFactorMilli: parseQty(fatores[l.id] ?? '') ?? undefined }
              : {}),
            batchCode: exigeLote(l) ? lotes[l.id]?.code.trim() || null : null,
            batchExpiry: exigeLote(l) ? lotes[l.id]?.expiry || null : null,
          })),
        },
      }),
    onSuccess: () => {
      onChanged();
      onClose();
    },
  });

  return (
    <>
      <Janela
        title="Conciliação de itens da nota"
        description="Vincule cada item da nota ao item do seu estoque e dê entrada no almoxarifado."
        wide
        onClose={onClose}
        footer={
          <>
            <Button
              variant="outline"
              icon={Pencil}
              onClick={() => void navigate({ to: documentoPath(doc.id) })}
            >
              Abrir a nota
            </Button>
            <span style={{ flex: 1 }} />
            <Button variant="outline" onClick={onClose} disabled={salvar.isPending}>
              {somenteLeitura ? 'Fechar' : 'Cancelar'}
            </Button>
            {!somenteLeitura && (
              <Button
                variant="primary"
                icon={Save}
                disabled={!prontas}
                loading={salvar.isPending}
                onClick={() => salvar.mutate()}
              >
                Salvar conciliação
              </Button>
            )}
          </>
        }
      >
        <div className="nf-kpis">
          <div className="nf-kpi">
            <FileText size={20} aria-hidden />
            <Dado label="Número da nota">
              <strong className="mono">
                {doc.number}
                {doc.series ? `/${doc.series}` : ''}
              </strong>
            </Dado>
          </div>
          <div className="nf-kpi">
            <Truck size={20} aria-hidden />
            <Dado label="Fornecedor">
              <strong>{doc.supplierTradeName ?? doc.supplierName}</strong>
              <span className="muted mono" style={{ fontSize: 11 }}>
                {doc.supplierDocument ? formatDocument(doc.supplierDocument) : ''}
              </span>
            </Dado>
          </div>
          <div className="nf-kpi">
            <CircleDollarSign size={20} aria-hidden />
            <Dado label="Valor total">
              <strong className="mono">{formatCents(doc.totalCents)}</strong>
            </Dado>
          </div>
          <div className="nf-kpi">
            <Dado label="Status da nota">
              <StatusBadge kind={SITUATION_TONE[doc.situation]}>
                {FISCAL_SITUATION_LABELS[doc.situation]}
              </StatusBadge>
              <span className="muted" style={{ fontSize: 11 }}>
                Emissão {formatDate(doc.issueDate)}
              </span>
            </Dado>
          </div>
          <div className="nf-kpi">
            <Boxes size={20} aria-hidden />
            <Dado label="Itens na nota">
              <strong>{doc.items.length}</strong>
            </Dado>
          </div>
          <div className="nf-kpi nf-kpi--progresso">
            <Dado label="Progresso da conciliação">
              <span className="nf-barra" aria-hidden>
                <span style={{ width: `${progresso}%` }} />
              </span>
              <span className="muted" style={{ fontSize: 11.5 }}>
                {vinculadas.length} de {doc.items.length} itens vinculados · {progresso}%
              </span>
            </Dado>
          </div>
        </div>

        {bloqueio && (
          <div className="nf-aviso" role="status">
            {bloqueio}
          </div>
        )}
        {salvar.isError && (
          <div className="login__alert" role="alert" style={{ margin: '12px 0' }}>
            {errorMessage(salvar.error)}
          </div>
        )}

        <div className="nf-itens">
          <section className="nf-itens__tabela" aria-label="Itens da nota">
            <div className="nf-secao-cab">
              <div>
                <h3 className="nf-secao-titulo">Itens da nota ({doc.items.length})</h3>
                <p className="nf-secao-desc">
                  Clique numa linha e escolha o item do estoque ao lado — ou direto na lista da
                  linha.
                </p>
              </div>
              {!somenteLeitura && (
                <label className="nf-local">
                  <span>Local de entrada</span>
                  <select
                    className="input"
                    value={locationId}
                    aria-label="Local de entrada"
                    onChange={(event) => setLocationId(event.target.value)}
                  >
                    <option value="">Selecione…</option>
                    {(locais.data ?? []).map((local) => (
                      <option key={local.id} value={local.id}>
                        {local.code} · {local.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>

            <table className="nf-tabela nf-tabela--itens">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Item da nota</th>
                  <th>Un.</th>
                  <th className="is-right">Qtde</th>
                  <th className="is-right">Valor total</th>
                  <th>Fator</th>
                  <th>Item do estoque</th>
                  <th className="is-right">Custo</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {doc.items.map((line, index) => {
                  const itemId = links[line.id] ?? '';
                  const item = itemId ? conhecido(itemId) : null;
                  const opcoes = [
                    ...(item && !(catalogo.data ?? []).some((c) => c.id === item.id) ? [item] : []),
                    ...(catalogo.data ?? []),
                  ];
                  return (
                    <tr
                      key={line.id}
                      className={linhaAtiva === line.id ? 'is-escolhida' : ''}
                      onClick={() => setLinhaAtiva(line.id)}
                    >
                      <td className="mono">{index + 1}</td>
                      <td>
                        <span className="nf-desc" title={line.description}>
                          {line.description}
                        </span>
                      </td>
                      <td className="mono">{line.unit}</td>
                      <td className="is-right mono">{formatQty(line.quantityMilli)}</td>
                      <td className="is-right mono">{formatCents(line.totalCents)}</td>
                      <td>
                        {unidadeDiferente(line) && !somenteLeitura ? (
                          <input
                            className="input input--mono nf-fator"
                            value={fatores[line.id] ?? ''}
                            placeholder="?"
                            aria-label={`Fator de conversão da linha ${index + 1}`}
                            title={`Quantos ${item?.unit} vêm em 1 ${line.unit}`}
                            onChange={(event) =>
                              setFatores((atual) => ({ ...atual, [line.id]: event.target.value }))
                            }
                          />
                        ) : (
                          <span className="mono muted">1</span>
                        )}
                      </td>
                      <td>
                        {somenteLeitura ? (
                          <span>{line.sku ?? '—'}</span>
                        ) : (
                          <>
                            <select
                              className="input nf-vinculo"
                              value={itemId}
                              aria-label={`Item do estoque da linha ${index + 1}`}
                              onChange={(event) => {
                                const escolhido = opcoes.find((o) => o.id === event.target.value);
                                if (escolhido) vincular(line.id, escolhido);
                                else desvincular(line.id);
                              }}
                            >
                              <option value="">Selecione um item</option>
                              {opcoes.map((o) => (
                                <option key={o.id} value={o.id}>
                                  {o.sku} · {o.name} ({o.unit})
                                </option>
                              ))}
                            </select>
                            {exigeLote(line) && (
                              <div className="nf-lote">
                                <input
                                  className="input"
                                  placeholder="Lote"
                                  aria-label={`Lote da linha ${index + 1}`}
                                  value={lotes[line.id]?.code ?? ''}
                                  onChange={(event) =>
                                    setLotes((atual) => ({
                                      ...atual,
                                      [line.id]: { code: event.target.value, expiry: atual[line.id]?.expiry ?? '' },
                                    }))
                                  }
                                />
                                <input
                                  className="input input--mono"
                                  type="date"
                                  aria-label={`Validade do lote da linha ${index + 1}`}
                                  value={lotes[line.id]?.expiry ?? ''}
                                  onChange={(event) =>
                                    setLotes((atual) => ({
                                      ...atual,
                                      [line.id]: { code: atual[line.id]?.code ?? '', expiry: event.target.value },
                                    }))
                                  }
                                />
                              </div>
                            )}
                          </>
                        )}
                      </td>
                      <td className="is-right mono">{formatCents(line.unitPriceCents)}</td>
                      <td>
                        {itemId || line.itemId ? (
                          <StatusBadge kind="ok">Vinculado</StatusBadge>
                        ) : (
                          <StatusBadge kind="warn">Pendente</StatusBadge>
                        )}
                      </td>
                      <td>
                        {!somenteLeitura && itemId && (
                          <button
                            type="button"
                            className="nf-acao nf-acao--perigo"
                            aria-label={`Tirar o vínculo da linha ${index + 1}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              desvincular(line.id);
                            }}
                          >
                            <X size={14} aria-hidden />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!somenteLeitura && semVinculo > 0 && vinculadas.length > 0 && (
              <p className="nf-secao-desc" style={{ marginTop: 8 }}>
                {semVinculo} linha(s) sem vínculo <strong>não entram</strong> no estoque — serviço,
                frete ou item que não se estoca.
              </p>
            )}
          </section>

          {!somenteLeitura && (
            <aside className="nf-itens__catalogo" aria-label="Itens do estoque">
              <h3 className="nf-secao-titulo">Itens do estoque</h3>
              <p className="nf-secao-desc">Busque e toque no + para vincular à linha marcada.</p>
              <div className="nf-busca">
                <Search size={15} aria-hidden />
                <input
                  className="input"
                  value={busca}
                  onChange={(event) => setBusca(event.target.value)}
                  placeholder="Digite para pesquisar…"
                  aria-label="Pesquisar item do estoque"
                />
              </div>
              <ul className="nf-catalogo">
                {(catalogo.data ?? []).map((item) => (
                  <li key={item.id}>
                    <span className="nf-catalogo__nome">
                      {item.name}
                      <span className="muted mono"> {item.sku}</span>
                    </span>
                    <span className="mono muted">{item.unit}</span>
                    {item.categoryName && <StatusBadge kind="info">{item.categoryName}</StatusBadge>}
                    <button
                      type="button"
                      className="nf-acao"
                      aria-label={`Vincular ${item.name} à linha marcada`}
                      disabled={!linhaAtiva}
                      onClick={() => linhaAtiva && vincular(linhaAtiva, item)}
                    >
                      <Plus size={15} aria-hidden />
                    </button>
                  </li>
                ))}
                {catalogo.data && catalogo.data.length === 0 && (
                  <li className="muted">Nenhum item com esse nome.</li>
                )}
              </ul>
              <Button
                small
                variant="ghost"
                icon={Plus}
                disabled={!linhaAtiva}
                onClick={() => {
                  const line = doc.items.find((l) => l.id === linhaAtiva);
                  if (line) setCadastrando(line);
                }}
              >
                Cadastrar a linha marcada como item novo
              </Button>
            </aside>
          )}
        </div>
      </Janela>

      {cadastrando && (
        <NovoItemModal
          key={cadastrando.id}
          line={cadastrando}
          onClose={() => setCadastrando(null)}
          onCreated={(item) => {
            vincular(cadastrando.id, item);
            setCadastrando(null);
          }}
        />
      )}
    </>
  );
}
